/****************************************************************************************
 * Speech Component is a LightningJS component that helps with Text-To-Speech. It
 * uses XDK's TTS module which is cross-platform.
 *
 *
 * Requirements
 * ------------
 * To be able to use this tool you'll need that your project is based on both
 * LightningJS and XDK and it also assumes you have XDK setup with the TTS extension
 * module loaded.
 * After XDK is initialized in your system you need to call SpeechComp.init() to
 * initialize this tool and set the activate property to true.
 * Usually the tool will read page -> section -> tile title. The idea is that page
 * will be read once to indicate the page the user is visiting and while section
 * will get read every time the user changes to a different section like "Continue
 * Watching" or "Most Popular". Tile title gets read each time a user changes tiles.
 *
 * How to Use
 * ----------
 *
 * speakCustom will read any text you pass to it.
 *
 * speakChildContent will read all text in the text property of all children of a
 * specific component.
 *
 * If you extend SpeechComp (like extend Tile from SpeechComp) then every time you focus
 * on a that object it will try to read page -> section -> title if any of those are set.
 * To set them we just need to create getters for each one get pageText for page,
 * get sectionText for section and speechText for the title. The current behaviour is to
 * try to find a pageText and/or sectionText property in any of the parents of the current
 * component. Also, pageText and sectionText will only be read when they change.
 *
 * The available methods on the XDK TTS extension are: speak, pause, resume, cancel.
 * We need to prevent an error in case one of them doesn't exist in the class.
 ***************************************************************************************/

import xdk from '@accedo/xdk-core'
import { tts } from '@accedo/xdk-extensions-contrib'
import { Lightning, Settings } from '@lightningjs/sdk'
import { AppConfig } from '../../services/appConfig'

export interface IPageSpeech extends Lightning.Element {
  readonly pageText: string
}

export interface ISectionSpeech extends Lightning.Element {
  readonly sectionText: string
}

/**
 * Speech Component is a LightningJS component that helps with Text-To-Speech. It
 * uses xdk's TTS module which is cross-platform.
 * If you extend SpeechComp (like extend Tile from SpeechComp) then every time you focus
 * on a that object it will try to read page -> section -> title if any of those are set.
 * To set them we just need to create getters for each one get pageText for page,
 * get sectionText for section and speechText for the title. The current behaviour is to
 * try to find a pageText and/or sectionText property in any of the parents of the current
 * component. Also, pageText and sectionText will only be read when they change.
 */
export default class SpeechComp extends Lightning.Component {
  /** The XDK's TTS instance. */
  private static _tts

  /** Keeps track of the page text. */
  private static _page: string = ''
  /** Keeps track of the section text. */
  private static _section: string = ''

  /** Indicates if it should read text (True) or not (False). */
  private static _active: boolean = false

  /** Indicates if it should ignore the route change and not read text. */
  private static _ignore: boolean = false

  /**
   * Getter for the text to speak.
   * Must be set on the components so text can be read.
   */
  get speechText() {
    return ''
  }

  /**
   * Getter for the page text.
   * Will try to find a pageText property in a parent of the current component.
   */
  get pageText() {
    let sText = ''
    let p = this.parent
    while (!sText && p) {
      if ('pageText' in p) {
        sText = (p as IPageSpeech).pageText
        break
      }
      p = p.parent
    }

    return sText
  }

  /**
   * Getter for the section text.
   * Will try to find a sectionText property in a parent of the current component.
   */
  get sectionText() {
    let sText = ''
    let p = this.parent
    while (!sText && p) {
      if ('sectionText' in p) {
        sText = (p as ISectionSpeech).sectionText
        break
      }
      p = p.parent
    }

    return sText
  }

  /**
   * Activates the component if True or deactivates if False.
   */
  static set active(v: boolean) {
    SpeechComp._active = v
  }

  /**
   * When the first instance of this component gets enabled then we try get
   * the tts instance from XDK.
   */
  async _enable() {
    if (!SpeechComp._tts) {
      const { ID: TTS } = tts
      SpeechComp._tts = await xdk.extensionManager.getExtension(TTS)
    }
  }

  /**
   * Reads the page, section and text of the current component.
   * Page and section texts are only read if they change.
   * It tries to cancel any ongoing speech.
   */
  protected speak(): void {
    let text = ''
    SpeechComp.logTTSText('[SPEECH] --------------- START -------------------------')

    if (!SpeechComp._tts || !SpeechComp._active || SpeechComp._ignore) {
      return
    }

    SpeechComp._tts?.cancel()

    SpeechComp.logTTSText(`[SPEECH] Current page text: ${this.pageText}`)

    if (this.pageText && SpeechComp._page != this.pageText) {
      SpeechComp._page = this.pageText
      SpeechComp.logTTSText(`[SPEECH] New page text: ${SpeechComp._page}`)
      text += `${SpeechComp._page + ' page'}. `
    }

    SpeechComp.logTTSText(`[SPEECH] Current section text: ${this.sectionText}`)

    if (this.sectionText && SpeechComp._section != this.sectionText) {
      SpeechComp._section = this.sectionText
      SpeechComp.logTTSText(`[SPEECH] New section text: ${SpeechComp._section}`)
      text += `${SpeechComp._section}. `
    }

    SpeechComp.logTTSText(`[SPEECH] Current node text: ${this.speechText}`)
    text += this.speechText

    SpeechComp.logTTSText(`[SPEECH] Final Text: ${text}`)
    SpeechComp._tts?.speak(SpeechComp.replaceFloSportsStringForSpeech(text))

    SpeechComp.logTTSText('[SPEECH] --------------- END -------------------------')
  }

  /**
   * Initializes this component.
   */
  public static async init(): Promise<void> {
    const onVoiceChange = (isVoiceOn) => (SpeechComp.active = isVoiceOn)
    AppConfig.shared.appSettings.device?.addVoiceChangeEventListener(onVoiceChange.bind(this))

    SpeechComp.active =
      (Settings.get('app', 'env.APP_ENABLE_TTS') ||
        AppConfig.shared.appSettings.device?.getVoiceState()) ??
      false
  }

  /**
   * Speaks any text passed to the function with an optional parameter
   * to stop or not any current text being read.
   * @param text - The text to read.
   * @param shouldCancel - If true cancels current on going speech otherwise
   * it just queues the text to be read after.
   */
  public static speakCustom(text: string, shouldCancel = false): void {
    if (!SpeechComp._tts || !SpeechComp._active) {
      return
    }

    if (shouldCancel) {
      SpeechComp._tts?.cancel()
    }

    SpeechComp._tts?.speak(SpeechComp.replaceFloSportsStringForSpeech(text))
  }

  /**
   * Tells the component to ignore the next text or not.
   */
  public static ignore(v: boolean): void {
    SpeechComp._ignore = v
  }

  /**
   * Reads all the text in all children of a specific component by looking at the text
   * properties of the children.
   * @param root The root node where to start looking for text.
   * @param shouldCancel If true it tries to stop the current text being read otherwise
   * it just queues the text to be read after.
   */
  public static speakChildContent(root: Lightning.Element, shouldCancel = false) {
    this.speakCustom(this.gatherText(root), shouldCancel)
  }

  /**
   * Auxiliary method to find the text in children of a specific node.
   * @param root The root node where to start looking for text.
   * @returns A string with all the text of the current node and its children.
   */
  private static gatherText(root: Lightning.Element): string {
    if (!root) {
      return ''
    }

    let text = root.text ? root.text.text : ''
    let el = root

    for (let i = 0; i < el.children.length; i++) {
      let child = el.children[i]
      if (child.text) {
        text += ' ' + child.text.text
      } else {
        text += this.gatherText(child)
      }
    }

    return text
  }

  private static replaceFloSportsStringForSpeech(v: string): string {
    return v.replace('FloSports', 'FlowSports')
  }

  private static logTTSText(text) {
    console.log(`%c${text}`, 'color: #87CEEB; font-weight: bold;')
  }

  public resetPageName() {
    SpeechComp._page = ''
  }
}
